import { useEffect } from 'react';
import { SHELL_TITLE, SHELL_DESCRIPTION, SHELL_URL, SHELL_OG_TYPE, SHELL_IMAGE, SHELL_IMAGE_ALT } from './shellHead';

/**
 * Per-route <head> management: title, description, canonical, Open Graph/Twitter
 * and JSON-LD.
 *
 * Until now the whole SPA shared the single static head in public/index.html, so
 * every URL reported the same title and description and no page had a canonical.
 * That is survivable for an app; it is fatal for articles that exist to rank.
 *
 * This generalises the imperative pattern CampaignLandingPage already uses (create
 * the node on mount, undo on cleanup) rather than introducing a head library
 * alongside it. Two reasons: it keeps one mechanism in the codebase instead of two,
 * and it avoids adding a dependency to the bootstrap path of a live, advertised
 * site for the sake of eleven pages.
 *
 * Cleanup restores the values from `shellHead`, NOT whatever was in the document on
 * mount. Article routes are prerendered, so on a direct visit the document already
 * holds that article's title, description and OG tags; treating those as "previous"
 * would carry the article's metadata onto the next route the reader visits.
 */

const MANAGED = 'data-managed-head';

const applyMeta = (attr, key, value) => {
  let node = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(attr, key);
    node.setAttribute(MANAGED, '');
    document.head.appendChild(node);
  }
  node.setAttribute('content', value);
};

/**
 * @param {object} head
 * @param {string} head.title           document title
 * @param {string} head.description     meta description
 * @param {string} head.canonical       absolute canonical URL
 * @param {string} [head.ogType]        og:type; 'website' for pages, 'article' for articles
 * @param {string} [head.image]         absolute URL of the link-preview image
 * @param {string} [head.imageAlt]      og:image:alt for that image (articles, SEO phase 1)
 * @param {string} [head.robots]        e.g. 'noindex, nofollow' for private routes
 * @param {object[]} [head.jsonLd]      structured-data objects, one <script> each
 */
const useDocumentHead = ({
  title, description, canonical, ogType = 'website', image, imageAlt, robots, jsonLd = [],
}) => {
  // Serialised so callers can build the schema objects inline, without memoising,
  // and still get a stable dependency.
  const jsonLdKey = JSON.stringify(jsonLd);

  useEffect(() => {
    if (!title) return undefined;

    // A prerendered document already carries the canonical and JSON-LD this hook
    // produces. Clear them first, or the page ships two of each.
    const dropManaged = () =>
      document.head.querySelectorAll(`[${MANAGED}]`).forEach(node => node.remove());

    const write = (values) => {
      document.title = values.title;
      applyMeta('name', 'description', values.description);
      applyMeta('property', 'og:title', values.title);
      applyMeta('property', 'og:description', values.description);
      applyMeta('property', 'og:url', values.url);
      applyMeta('property', 'og:type', values.ogType);
      applyMeta('property', 'og:image', values.image);
      applyMeta('property', 'og:image:alt', values.imageAlt);
      applyMeta('name', 'twitter:title', values.title);
      applyMeta('name', 'twitter:description', values.description);
      applyMeta('name', 'twitter:image', values.image);
    };

    dropManaged();
    write({
      title, description, url: canonical, ogType,
      image: image || SHELL_IMAGE,
      imageAlt: image ? (imageAlt || title) : SHELL_IMAGE_ALT,
    });

    // Private and token-bearing routes. /share/:token points at a family's memorial
    // video: being unlinked is not the same as being unindexable once a link is
    // pasted somewhere public.
    if (robots) {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      meta.setAttribute('content', robots);
      meta.setAttribute(MANAGED, '');
      document.head.appendChild(meta);
    }

    if (canonical) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canonical);
      link.setAttribute(MANAGED, '');
      document.head.appendChild(link);
    }

    for (const block of JSON.parse(jsonLdKey)) {
      if (!block) continue;
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(MANAGED, '');
      script.textContent = JSON.stringify(block);
      document.head.appendChild(script);
    }

    return () => {
      dropManaged();
      write({
        title: SHELL_TITLE,
        description: SHELL_DESCRIPTION,
        url: SHELL_URL,
        ogType: SHELL_OG_TYPE,
        image: SHELL_IMAGE,
        imageAlt: SHELL_IMAGE_ALT,
      });
    };
  }, [title, description, canonical, ogType, image, imageAlt, robots, jsonLdKey]);
};

export default useDocumentHead;
